import React, { useState, useCallback, useEffect } from 'react';
import { icons } from '../../../assets/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './index.scss';
import useOnclickOutside from 'react-cool-onclickoutside';
import { deleteOrderByAdmin, getOrdersByAdmin, addDelivery } from '../../../api/redux/slices/Admin';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { convertDate, generateId } from '../../../helpers/modules';
import { TopBar } from '../../../components/navbar';
import { LoaderComponent } from '../../../helpers/components/loader';
import Pagination from '../../../helpers/components/Pagination';
import DeleteConfirmationModal from '../addUser/ShopDeleteModal';
import useStableArr from '../../../utils/useStableArr';
import { showToast } from '../../../components/modal';
import { handleCheck, handleCheckAll } from '../../../utils/checkUtilsAdmin';

const OrdersPage = () => {
	const { listOrders, checkedList, total } = useSelector((state) => state.admin);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const params = useParams();
	const token = localStorage.getItem('accesstoken');

	const [orderOpenDropdown, setOrderOpenDropdown] = useState(false);
	const [indiceOrder, setIndiceOrder] = useState(null);
	const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
	const [modalCsvexport, setModalCsvexport] = useState(false);
	const [page, setPage] = useState(Number(params.page));
	const [searchValue, setSearchValue] = useState('');
	const [statusValue, setStatusValue] = useState('');
	const [isCheck, setIsCheck] = useState(false);
	const [newArr, setNewArr] = useState(
		listOrders.map((el) => ({ ...el, checked: false, custom_id: generateId() })),
	);
	const [loadingAccept, setLoadingAccept] = useState(false);

	const ref = useOnclickOutside(() => {
		setOrderOpenDropdown(false);
	});

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteIndex, setDeleteIndex] = useState(null);
	const [isloading, setIsLoading] = useState(false);

	const stableDispatch = useCallback(async () => {
		setIsLoading(true);
		dispatch(
			getOrdersByAdmin({
				token: token,
				page: searchValue || statusValue ? undefined : page,
				search: searchValue,
				status: statusValue,
			}),
		).finally(() => setIsLoading(false));
	}, [dispatch, page, token, searchValue, statusValue]);

	useEffect(() => {
		if (token) {
			stableDispatch();
		}
	}, [stableDispatch, token]);

	useStableArr(listOrders, checkedList, setNewArr, setIsCheck);

	// ✅ Fermer le dropdown au scroll
	useEffect(() => {
		const handleScroll = () => setOrderOpenDropdown(false);
		window.addEventListener('scroll', handleScroll, true);
		return () => window.removeEventListener('scroll', handleScroll, true);
	}, []);

	// ✅ Calculer la position exacte du bouton cliqué
	const handleToggleDropdown = (e, id) => {
		const rect = e.currentTarget.getBoundingClientRect();
		if (orderOpenDropdown && indiceOrder === id) {
			setOrderOpenDropdown(false);
		} else {
			setDropdownPos({
				top: rect.bottom + 4,
				left: rect.left - 160,
			});
			setOrderOpenDropdown(true);
			setIndiceOrder(id);
		}
	};

	const modifyOrder = (id) => {
		navigate(`/admin/orders/order_update/${params.page}/${id}`);
	};

	const headers = [
		{ label: 'NUMERO', key: 'number' },
		{ label: 'REFERENCE', key: 'reference' },
		{ label: 'DATE', key: 'date' },
		{ label: 'TIMBRE', key: 'timbre' },
		{ label: 'TOTAL', key: 'total' },
		{ label: 'TOTAL HT', key: 'total_HT' },
		{ label: 'TVA', key: 'tva' },
		{ label: 'CATEGORIE', key: 'clientInfo.categories' },
		{ label: 'NUMERO DE TEL', key: 'clientInfo.phone' },
		{ label: 'UTILISATEUR', key: 'clientInfo.client' },
		{ label: 'SOCIETE', key: 'clientInfo.company_name' },
	];

	const csvOrdersReport = {
		filename: 'Commandes.csv',
		data: checkedList,
		headers: headers,
	};

	const handleDeleteOneOrder = async (deleteIndex) => {
		try {
			await dispatch(deleteOrderByAdmin({ token: token, id: deleteIndex })).unwrap();
			return { success: true, error: null };
		} catch (err) {
			return { success: false, error: err.message || 'Erreur lors de la suppression' };
		}
	};

	const deleteAllChecked = async () => {
		const checkedOrderIds = checkedList.map((order) => order._id);
		if (checkedOrderIds.length === 0) {
			showToast('Veuillez sélectionner au moins une commande', 'warn');
			return false;
		}
		let successCount = 0;
		let errorCount = 0;
		let errorMessages = [];
		for (const orderId of checkedOrderIds) {
			const result = await handleDeleteOneOrder(orderId);
			if (result.success) {
				successCount++;
			} else {
				errorCount++;
				errorMessages.push(result.error);
			}
		}
		dispatch(getOrdersByAdmin({ token: token, page: page, search: searchValue, status: statusValue }));
		if (successCount > 0 && errorCount === 0) {
			toast.success(`${successCount} commande(s) supprimée(s) avec succès`, { position: 'top-center', autoClose: 2000 });
			return true;
		} else if (successCount > 0 && errorCount > 0) {
			toast.warning(`${successCount} supprimée(s), ${errorCount} échec(s)`, { position: 'top-center', autoClose: 3000 });
			if (errorMessages.length > 0) showToast(errorMessages[0], 'error');
			return false;
		} else {
			toast.error(errorMessages[0] || 'Échec de la suppression des commandes', { position: 'top-center', autoClose: 3000 });
			return false;
		}
	};

	const handleAcceptOrder = async (orderId) => {
		setLoadingAccept(true);
		try {
			await dispatch(addDelivery({ token, companyOrderId: orderId })).unwrap();
			showToast('Commande acceptée et livraison créée avec succès', 'success');
			dispatch(getOrdersByAdmin({ token: token, page: page, search: searchValue, status: statusValue }));
			setOrderOpenDropdown(false);
		} catch (err) {
			const errorMsg = err?.message || err?.response?.data?.message || "Erreur lors de l'acceptation de la commande";
			showToast(errorMsg, 'error');
		} finally {
			setLoadingAccept(false);
		}
	};

	return (
		<div id='orderPageAdmin'>
			{showDeleteModal && (
				<DeleteConfirmationModal
					onDelete={async () => {
						if (deleteIndex !== null) {
							const result = await handleDeleteOneOrder(deleteIndex);
							setOrderOpenDropdown(false);
							dispatch(getOrdersByAdmin({ token: token, page: page, search: searchValue, status: statusValue }));
							if (result.success) {
								toast.success('Commande supprimée avec succès', { position: 'top-center', autoClose: 1000 });
							} else {
								toast.error(result.error, { position: 'top-center', autoClose: 3000 });
							}
							setShowDeleteModal(false);
							setDeleteIndex(null);
						} else {
							await deleteAllChecked();
							setShowDeleteModal(false);
							setDeleteIndex(null);
						}
					}}
					onCancel={() => {
						setShowDeleteModal(false);
						setDeleteIndex(null);
					}}
					msg={`${deleteIndex !== null
						? 'Êtes-vous sûr de vouloir supprimer cette commande ?'
						: 'Êtes-vous sûr de vouloir supprimer toutes les commandes sélectionnées ?'
					}`}
				/>
			)}

			<TopBar
				searchValue={searchValue}
				setSearchValue={setSearchValue}
				modalCsvexport={modalCsvexport}
				setModalCsvexport={setModalCsvexport}
				statusValue={statusValue}
				setStatusValue={setStatusValue}
				csvOrdersReport={csvOrdersReport}
			/>

			<div id='head'>
				<div><h1>Bons de commandes</h1></div>
				<div
					className='deluser'
					onClick={() => {
						const hasAcceptedOrder = checkedList.some((order) => order.status === 'ACCEPTED');
						if (hasAcceptedOrder) {
							showToast('Vous ne pouvez pas supprimer une commande déjà acceptée', 'error');
							return;
						}
						if (checkedList.length > 0) {
							setShowDeleteModal(true);
							setDeleteIndex(null);
						} else {
							showToast('Choisissez au moins une commande.', 'warn');
						}
					}}
				>
					<icons.FaTrashAlt className='trash' />
				</div>
			</div>

			<div id='ordersContentAdmin'>
				<table id='orders'>
					<thead>
						<tr id='titres'>
							<td className='input-check'>
								<input type='checkbox' className='checkbox' checked={isCheck} onChange={() => handleCheckAll(dispatch, newArr, setNewArr, isCheck, setIsCheck)} />
							</td>
							<th>NUM</th>
							<th>DATE</th>
							<th>CLIENT</th>
							<th>SOCIÉTÉ</th>
							<th>TOTAL</th>
							<td className='center'>STATUT</td>
							<th>ACTION</th>
						</tr>
					</thead>
					<tbody>
						{isloading ? (
							<tr><td colSpan='8'><LoaderComponent margin='10%' /></td></tr>
						) : newArr?.length > 0 ? (
							newArr?.map((order, index) => (
								<tr
									className='order'
									key={index}
									// ✅ FIX : la ligne active passe au-dessus des autres
									style={{ zIndex: orderOpenDropdown && indiceOrder === order._id ? 1000 : 1 }}
								>
									<td className='input-check'>
										<input type='checkbox' className='checkbox' checked={order.checked} onChange={() => handleCheck(dispatch, newArr, setNewArr, order._id)} />
									</td>
									<td style={{ textTransform: 'uppercase' }}>{order?.reference || '-'}</td>
									<td>{order?.date ? convertDate(order?.date, 'DD-MM-YYYY') : '-'}</td>
									<td>{order?.clientInfo?.client || '-'}</td>
									<td>{order?.clientInfo?.company_name || '-'}</td>
									<td>{order?.total != null ? Number(order.total).toFixed(2) : '-'} DT</td>
									<td className={`status ${order?.status === 'ORDER' ? 'en-cours' : 'confirmer'}`}>
										{order?.status === 'ORDER' ? 'en cours' : 'confirmé'}
									</td>
									<td className='last-order-element'>
										<Link to={`/admin/orders/order_details/${page}/${order.parentOrderId}`} className='link-order'>
											<icons.BsEye className='see-order' />
										</Link>
										<div className='contain'>
											<icons.BsThreeDotsVertical
												onClick={(e) => handleToggleDropdown(e, order._id)}
												className='order-option'
											/>
											{orderOpenDropdown && indiceOrder === order._id && (
												<div
													className='modalSelectOpt'
													ref={ref}
													style={{
														position: 'fixed',
														top: `${dropdownPos.top}px`,
														left: `${dropdownPos.left}px`,
														zIndex: 99999,
													}}
												>
													<ul>
														<li onClick={() => modifyOrder(order?.parentOrderId)}>
															Modifier commande
														</li>
														{order?.status !== 'ACCEPTED' && (
															<>
																<li onClick={() => handleAcceptOrder(order?._id)}>
																	{loadingAccept ? 'Chargement...' : 'Faites-en une livraison'}
																</li>
																<li onClick={() => { setDeleteIndex(order?._id); setShowDeleteModal(true); }}>
																	Supprimer commande
																</li>
															</>
														)}
													</ul>
												</div>
											)}
										</div>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td colSpan='8' style={{ textAlign: 'center', padding: '50px' }}>
									Aucune commande trouvée
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<Pagination page={page} setPage={setPage} pageLink={'/admin/orders/'} total={total} />
		</div>
	);
};

export default OrdersPage;